// reviewsReducer.js
// Redux reducer for managing assessment reviews state

import {
  FETCH_REVIEWS_START,
  FETCH_REVIEWS_SUCCESS,
  FETCH_REVIEWS_FAILURE,
  SUBMIT_FOR_REVIEW,
  SUBMIT_FOR_REVIEW_SUCCESS,
  SUBMIT_FOR_REVIEW_FAILURE,
  UPDATE_REVIEW_STATUS,
  UPDATE_REVIEW_STATUS_SUCCESS,
  UPDATE_REVIEW_STATUS_FAILURE,
  START_REVIEW,
  START_REVIEW_SUCCESS,
  START_REVIEW_FAILURE,
  UPDATE_REVIEW_PROGRESS,
  TRACK_EDIT,
  TRACK_EDIT_SUCCESS,
  TRACK_EDIT_FAILURE,
  GENERATE_LLM_REVIEW,
  GENERATE_LLM_REVIEW_SUCCESS,
  GENERATE_LLM_REVIEW_FAILURE
} from '../actions/reviewActions';

/**
 * Reviews state structure:
 * {
 *   byModel: {
 *     [modelId]: {
 *       assessmentUuid: string,
 *       reviewStatus: 'pending' | 'submitted' | 'approved' | 'rejected',
 *       requestedBy: {
 *         email: string,
 *         name: string,
 *         requestedAt: timestamp
 *       },
 *       reviewedBy: {
 *         email: string,
 *         name: string,
 *         reviewedAt: timestamp
 *       },
 *       reviewResults: {
 *         comments: string,
 *         changes: []
 *       }
 *     }
 *   },
 *   loading: boolean,
 *   error: string | null
 * }
 */

const initialState = {
  byModel: {},
  loading: false,
  error: null
};

export default function reviewsReducer(state = initialState, action) {
  switch (action.type) {
    case FETCH_REVIEWS_START:
    case SUBMIT_FOR_REVIEW:
    case UPDATE_REVIEW_STATUS:
      return {
        ...state,
        loading: true,
        error: null
      };

    case FETCH_REVIEWS_FAILURE:
    case SUBMIT_FOR_REVIEW_FAILURE:
    case UPDATE_REVIEW_STATUS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    case FETCH_REVIEWS_SUCCESS:
      return {
        ...state,
        loading: false,
        byModel: {
          ...state.byModel,
          ...action.payload.reviews
        }
      };

    case SUBMIT_FOR_REVIEW_SUCCESS:
      return {
        ...state,
        loading: false,
        byModel: {
          ...state.byModel,
          [action.payload.modelId]: {
            assessmentUuid: action.payload.assessmentUuid,
            reviewStatus: action.payload.reviewStatus,
            requestedBy: action.payload.requestedBy,
            reviewedBy: null,
            reviewResults: null
          }
        }
      };

    case UPDATE_REVIEW_STATUS_SUCCESS:
      return {
        ...state,
        loading: false,
        byModel: {
          ...state.byModel,
          [action.payload.modelId]: {
            ...state.byModel[action.payload.modelId],
            reviewStatus: action.payload.reviewStatus,
            reviewedBy: action.payload.reviewedBy,
            reviewResults: action.payload.reviewResults
          }
        }
      };

    case START_REVIEW:
    case GENERATE_LLM_REVIEW:
      return {
        ...state,
        loading: true,
        error: null
      };

    case START_REVIEW_FAILURE:
    case GENERATE_LLM_REVIEW_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    case START_REVIEW_SUCCESS: {
      const { modelId, assessmentUuid, reviewStatus, startedBy } = action.payload;
      return {
        ...state,
        loading: false,
        byModel: {
          ...state.byModel,
          [modelId]: {
            ...state.byModel[modelId],
            assessmentUuid,
            reviewStatus,
            startedBy,
            currentStep: 'risks',
            currentRiskId: null,
            currentThreatId: null,
            progress: {
              risksReviewed: 0,
              totalRisks: 0,
              threatsReviewed: 0,
              totalThreats: 0,
              reviewedRiskIds: [],
              reviewedThreatIds: []
            },
            edits: {
              risks: {},
              controls: {},
              residualRisks: {},
              threats: {},
              securityControls: {}
            },
            llmReviews: {}
          }
        }
      };
    }

    case UPDATE_REVIEW_PROGRESS: {
      const { modelId, step, itemId } = action.payload;
      const currentModel = state.byModel[modelId] || {};

      return {
        ...state,
        byModel: {
          ...state.byModel,
          [modelId]: {
            ...currentModel,
            currentStep: step,
            ...(step === 'risks' ? { currentRiskId: itemId } : {}),
            ...(step === 'security' ? { currentThreatId: itemId } : {})
          }
        }
      };
    }

    case TRACK_EDIT: {
      const { modelId, itemType, itemId, field, oldValue, newValue, userInfo } = action.payload;
      const currentModel = state.byModel[modelId] || {};
      const editsCategory = getEditsCategory(itemType);
      const currentEdits = currentModel.edits?.[editsCategory] || {};
      const currentItemEdits = currentEdits[itemId] || { editedBy: userInfo, editedAt: new Date().toISOString(), changes: [] };

      return {
        ...state,
        byModel: {
          ...state.byModel,
          [modelId]: {
            ...currentModel,
            edits: {
              ...currentModel.edits,
              [editsCategory]: {
                ...currentEdits,
                [itemId]: {
                  ...currentItemEdits,
                  editedBy: userInfo,
                  editedAt: new Date().toISOString(),
                  changes: [
                    ...currentItemEdits.changes,
                    {
                      field,
                      oldValue,
                      newValue,
                      timestamp: new Date().toISOString()
                    }
                  ]
                }
              }
            }
          }
        }
      };
    }

    case TRACK_EDIT_SUCCESS:
    case TRACK_EDIT_FAILURE:
      // These don't change state - tracking happens in TRACK_EDIT
      return state;

    case GENERATE_LLM_REVIEW_SUCCESS: {
      const { modelId, itemType, itemId, feedback, customInstructions } = action.payload;
      const currentModel = state.byModel[modelId] || {};

      return {
        ...state,
        loading: false,
        byModel: {
          ...state.byModel,
          [modelId]: {
            ...currentModel,
            llmReviews: {
              ...currentModel.llmReviews,
              [itemId]: {
                generatedAt: new Date().toISOString(),
                customInstructions,
                feedback,
                itemType
              }
            }
          }
        }
      };
    }

    default:
      return state;
  }
}

/**
 * Helper function to map item type to edits category
 * Keeps naming consistent and avoids hard-coding
 */
function getEditsCategory(itemType) {
  const categoryMap = {
    'risk': 'risks',
    'control': 'controls',
    'residual_risk': 'residualRisks',
    'threat': 'threats',
    'security_control': 'securityControls'
  };
  return categoryMap[itemType] || itemType;
}
